/**
 * The render half of the admin usage page (ATRU-06; COP-134 D4).
 *
 * Split out of `Usage.tsx` for the same reason `pages/sessions/session-
 * transcript.tsx` and `pages/schedules/run-history-row.tsx` are: `Usage.tsx`
 * imports `@/lib/hub-client`, which constructs a `HubClient` at MODULE load and
 * throws under vitest (`baseUrl not provided`). A test can import this file.
 *
 * Controlled and pure — the container owns the two control values and the
 * request; this component only renders. Every sentence comes from
 * `usage-ui-model.ts`; none is composed here.
 */
import { DbChartECharts } from '@verybigthings/trackframe-hub-client/ui/components/primitives';
import type { UsageSummaryGroupBy, UsageSummaryResponse } from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  USAGE_EMPTY_COPY,
  USAGE_GROUP_BY_LABELS,
  USAGE_GROUP_BY_OPTIONS,
  USAGE_WINDOW_PRESETS,
  buildUsageChart,
  buildUsageTableRows,
  usageErrorCopy,
  usageRetentionSentence,
  usageRetentionWarning,
  usageRowCapNote,
  usageTotalSentence,
  usageWindowSentence,
} from './usage-ui-model';

export type UsageViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: UsageSummaryResponse }
  | { kind: 'error'; httpStatus: number | null; message: string };

export interface UsageViewProps {
  state: UsageViewState;
  windowDays: number;
  groupBy: UsageSummaryGroupBy;
  onWindowDays(days: number): void;
  onGroupBy(groupBy: UsageSummaryGroupBy): void;
}

/** The two controls. There is deliberately no third (COP-134 D4). */
function UsageControls({
  windowDays,
  groupBy,
  onWindowDays,
  onGroupBy,
}: Omit<UsageViewProps, 'state'>) {
  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1.5">
        <Label htmlFor="usage-window">Window</Label>
        <Select
          id="usage-window"
          className="w-40"
          value={String(windowDays)}
          onChange={(e) => onWindowDays(Number(e.target.value))}
        >
          {USAGE_WINDOW_PRESETS.map((days) => (
            <option key={days} value={days}>
              Last {days} days
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="usage-group-by">Group by</Label>
        <Select
          id="usage-group-by"
          className="w-40"
          value={groupBy}
          onChange={(e) => onGroupBy(e.target.value as UsageSummaryGroupBy)}
        >
          {USAGE_GROUP_BY_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {USAGE_GROUP_BY_LABELS[option]}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

function UsageSummaryBody({ data }: { data: UsageSummaryResponse }) {
  const chart = buildUsageChart(data);
  const tableRows = buildUsageTableRows(data);
  const warning = usageRetentionWarning(data);
  const capNote = usageRowCapNote(data);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          {/* Dimension-scoped by contract, so the sentence names its dimension
              (COP-134 D4) — a bare number moves when the selector moves. */}
          <CardTitle className="text-xl tabular-nums" data-testid="usage-total">
            {usageTotalSentence(data)}
          </CardTitle>
          <CardDescription data-testid="usage-window">{usageWindowSentence(data)}</CardDescription>
          <CardDescription data-testid="usage-retention">
            {usageRetentionSentence(data)}
          </CardDescription>
        </CardHeader>
        {warning && (
          <CardContent>
            <Alert variant="warning" data-testid="usage-retention-warning">
              {warning}
            </Alert>
          </CardContent>
        )}
      </Card>

      {tableRows.length === 0 ? (
        // Never an empty chart canvas — an empty axis reads as a broken page.
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground" data-testid="usage-empty">
            {USAGE_EMPTY_COPY}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="pt-6">
              <DbChartECharts type="bar" columns={chart.columns} rows={chart.rows} />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{USAGE_GROUP_BY_LABELS[data.groupBy]}</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="font-medium">{row.label}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.countText}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {capNote && (
            <p className="text-sm text-muted-foreground" data-testid="usage-row-cap">
              {capNote}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function UsageView({ state, ...controls }: UsageViewProps) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Usage</h1>
        <p className="text-sm text-muted-foreground">
          Recorded activity from the audit log — who and what the hub is being used for.
        </p>
      </div>

      <UsageControls {...controls} />

      {state.kind === 'loading' && <p className="text-sm text-muted-foreground">Loading…</p>}
      {state.kind === 'error' && (
        <Alert data-testid="usage-error">{usageErrorCopy(state.httpStatus, state.message)}</Alert>
      )}
      {state.kind === 'ready' && <UsageSummaryBody data={state.data} />}
    </div>
  );
}
